import type {
  Thread,
  Candidate,
  EmailMessage,
  Mail,
} from 'src/types/crm-mailbox';

import {showApiError} from 'src/action-creators/modal';
import type {Dispatch, ThunkAction} from 'src/reducers';
import {thunkify as flow} from 'src/utils/thunks';
import * as reduxApiBff from 'src/utils/redux-api-bff';
import {key, fetching} from 'src/utils/redux';


const BASE_API_PATH = 'mailbox';
export const MAILBOX_ERROR = 'crm-mailbox/error';
export const GET_MAILBOX_THREADS = 'crm-mailbox/mailbox-threads';
export const GET_CANDIDATE_DETAILS = 'crm-mailbox/candidate-details';
export const GET_EMAIL_MESSAGES = 'crm-mailbox/email-messages';
export const UPDATE_THREAD_READ_STATUS =
  'crm-mailbox/update-thread-read-status';
export const GET_RELATED_THREADS = 'crm-mailbox/related-threads';

export const SEND_MAIL = 'crm-mailbox/send-mail';

type GetMailboxError = {
  type: typeof MAILBOX_ERROR;
  payload: unknown;
};

type GetMailboxThreads = {
  type: typeof GET_MAILBOX_THREADS;
  payload: Thread[];
  meta: {
    pageNumber: number;
    messageBoxType: string;
  };
};

type GetRelatedThreads = {
  type: typeof GET_RELATED_THREADS;
  payload: Thread[];
};

type GetCandidateDetails = {
  type: typeof GET_CANDIDATE_DETAILS;
  payload: Candidate;
};

type GetEmailMessages = {
  type: typeof GET_EMAIL_MESSAGES;
  payload: EmailMessage[];
};

type UpdateThreadReadStatus = {
  type: typeof UPDATE_THREAD_READ_STATUS;
  payload: {
    threadId: number;
    readStatus: boolean;
  };
};
type SendMail = {
  type: typeof SEND_MAIL;
  payload: Thread;
};

export type UserMailboxActions =
  | GetMailboxError
  | GetMailboxThreads
  | GetCandidateDetails
  | GetEmailMessages
  | SendMail
  | GetRelatedThreads;

const getMailboxError = (error: unknown): GetMailboxError => ({
  type: MAILBOX_ERROR,
  payload: error,
});

const getMailboxThreads = (
  threads: Thread[],
  pageNumber: number,
  messageBoxType: string,
): GetMailboxThreads => ({
  type: GET_MAILBOX_THREADS,
  payload: threads,
  meta: {pageNumber, messageBoxType},
});

const getCandidateDetails = (candidate: Candidate): GetCandidateDetails => ({
  type: GET_CANDIDATE_DETAILS,
  payload: candidate,
});

const getEmailMessages = (messages: EmailMessage[]): GetEmailMessages => ({
  type: GET_EMAIL_MESSAGES,
  payload: messages,
});

const updateThreadReadStatus = (
  threadId: number,
  readStatus: boolean,
): UpdateThreadReadStatus => ({
  type: UPDATE_THREAD_READ_STATUS,
  payload: {threadId, readStatus},
});

const getRelatedThreads = (threads: Thread[]): GetRelatedThreads => ({
  type: GET_RELATED_THREADS,
  payload: threads,
});

export const fetchMailboxThreads: (
  mailboxId: number,
  pageNumber: number,
  pageSize: number,
  boxId: string,
) => ThunkAction<void> = flow(
  key(
    (mailboxId, pageNumber, boxId) =>
      `/mailbox/candidate-threads/${mailboxId}-${pageNumber}-${boxId}`,
  ),
  fetching(),
)(
  (mailboxId: number, pageNumber: number, pageSize: number, boxId: string) =>
    async (dispatch: Dispatch) => {
      try {
        const response: {
          threads: Thread[];
        } = await dispatch(
          reduxApiBff.post(`${BASE_API_PATH}/threads`, {
            pageNumber,
            pageSize,
            messageBoxType: boxId,
            userEmailId: mailboxId,
          }),
        );

        if (response.threads) {
          // @ts-expect-error - TS2769 - No overload matches this call.
          dispatch(getMailboxThreads(response.threads, pageNumber, boxId));
        }
      } catch (error: any) {
        console.error('Error fetching mailbox threads:', error);
      }
    },
);

export const fetchCandidateDetails: (threadId: number) => ThunkAction<void> =
  flow(
    key((threadId) => `/mailbox/candidate-details/${threadId}`),
    fetching(),
  )((threadId) => (dispatch: Dispatch) => {
    return dispatch(
      reduxApiBff.post(`${BASE_API_PATH}/thread/candidate`, {
        threadId,
      }),
    ).then((response: {candidate: Candidate}) => {
      // @ts-expect-error - TS2769 - No overload matches this call.
      dispatch(getCandidateDetails(response.candidate));
    });
  });

export const fetchEmailMessages: (threadId: number) => ThunkAction<void> = flow(
  key((threadId) => `/mailbox/email-messages/${threadId}`),
  fetching(),
)((threadId) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApiBff.post(`${BASE_API_PATH}/thread/messages`, {
      threadId,
      pageNumber: 0,
      pageSize: 100,
    }),
  ).then((response: {messages: EmailMessage[]}) => {
    // @ts-expect-error - TS2769 - No overload matches this call.
    dispatch(getEmailMessages(response.messages));
  });
});

export const markAsReadUnread: (
  threadId: number,
  readStatus: boolean,
) => ThunkAction<void> = flow(
  key(
    (threadId, readStatus) =>
      `/mailbox/update-read-status/${threadId}-${readStatus}`,
  ),
  fetching(),
)((threadId, readStatus) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApiBff.post(`${BASE_API_PATH}/thread/update-read-status`, {
      threadId,
      readStatus,
    }),
  ).then((response: {readStatus: boolean}) => {
    // @ts-expect-error - TS2769 - No overload matches this call.
    dispatch(updateThreadReadStatus(threadId, response.readStatus));
  });
});

export const fetchReleatedThreads: (
  threadId: number,
  userEmailId: number,
) => ThunkAction<void> = flow(
  key((threadId) => `/mailbox/related-threads/${threadId}`),
  fetching(),
)((threadId: number, userEmailId: number) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApiBff.post(`${BASE_API_PATH}/related-threads`, {
      threadId,
      userEmailId,
      pageNumber: 0,
      pageSize: 100,
    }),
  ).then((response: {threads: Thread[]}) => {
    // @ts-expect-error - TS2769 - No overload matches this call.
    dispatch(getRelatedThreads(response.threads));
  });
});
